import React from 'react';
import { useTranslation } from 'react-i18next';

import { ISelectOption } from '@private/components';
import { NOTIF_TYPES } from '@private/notifications';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import api from 'api';
import { PERMISSIONS } from 'api/auth/constants';
import { MERCHANTS_KEYS } from 'api/merchants/constants';
import {
  IActivateCascadingSchemaPayload,
  IActivateRoutingSchemaPayload,
  IApiUser,
  IApiUserEdit,
  ICascadingSchema,
  ICascadingSchemaDetails,
  ICascadingSchemaPayload,
  ICreateApiUserPayload,
  ICustomAction,
  ICustomActionPayload,
  IDeleteSchemaPayload,
  IEditMerchantOrganizationData,
  IEditMerchantPayload,
  IGetMerchantOutputChannelsResult,
  IGetMerchantRoutingRulesetsPayload,
  IGetMerchantRoutingRulesetsResult,
  IMerchant,
  IMerchantClientsLimits,
  IMerchantClientsLimitsPayload,
  IMerchantDetails,
  IMerchantRoutingSchemas,
  IMerchantsScope,
  IRoutingSchema,
  IRoutingSchemaPayload,
  IUpdateMerchantSingleParameterPayload,
  IVerifyMerchantOutputChannelPayload,
} from 'api/merchants/types';
import { IOutputChannel } from 'api/notification/types';
import { IOutputChannelPayload } from 'components/OutputChannelSidebar/types';
import { STATUSES } from 'constants/common';
import { useRBAC } from 'hooks/useRBAC';
import { normalizeQueryKey, useGetAllEntities } from 'queries/utils';
import {
  ICopyMerchantForm,
  IMerchantForm,
} from 'routes/pages/AuthorizedStack/Merchants/Merchant/Form/helpers/types';
import {
  getInlineEditErrorMessage,
  getQueryBoundary,
  showMutationErrorNotification,
  translateServerMessage,
} from 'utils/common';
import { notificationService } from 'utils/notificationService';
import {
  IAllPageFilters,
  IBaseGist,
  IGroupEditPayload,
  INullable,
  IPageType,
  IUseGetGridData,
  QueryError,
} from 'utils/types';

export const useGetMerchants = (
  pageFilters: IAllPageFilters,
  viewOnlyActive = false,
): UseQueryResult<IPageType<IMerchant>> => {
  const queryDeps = React.useMemo(() => [viewOnlyActive], [viewOnlyActive]);

  return useGetAllEntities<IMerchant>({
    getAllEntitiesFunc: (filters) =>
      api.merchants.getAllMerchants(filters, viewOnlyActive),
    pageFilters,
    queryDeps,
    queryKey: MERCHANTS_KEYS.ALL,
  });
};

export const useGetMerchant = (
  id?: INullable<number | string>,
  enabled = true,
): UseQueryResult<IMerchantDetails> => {
  const getMerchantDetails = React.useCallback(
    () => (id ? api.merchants.getMerchant(id) : null),
    [id],
  );

  return useQuery({
    enabled,
    queryFn: getMerchantDetails,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT, id]),
  });
};

export const useGetMerchantCascadingSchemaList = (
  id?: INullable<number | string>,
): UseQueryResult<ICascadingSchema[]> => {
  const { enabled } = useRBAC({ list: [PERMISSIONS.CAN_VIEW_CASCADING_SCHEMAS] });

  const getMerchantCascadingSchemaList = React.useCallback(
    () => (id && enabled ? api.merchants.getMerchantCascadingSchemaList(id) : null),
    [enabled, id],
  );

  return useQuery({
    queryFn: getMerchantCascadingSchemaList,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMAS, id]),
  });
};

export const useGetMerchantRoutingSchemaList = (
  id?: INullable<number | string>,
): UseQueryResult<IRoutingSchema[]> => {
  const getMerchantRoutingSchemaList = React.useCallback(
    () => (id ? api.merchants.getMerchantRoutingSchemaList(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getMerchantRoutingSchemaList,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.ROUTING_SCHEMAS, id]),
  });
};

export const useGetMerchantsNames = (): UseQueryResult<ISelectOption[]> => {
  return useQuery({
    queryFn: api.merchants.getMerchantNames,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANTS_NAMES]),
  });
};

export const useGetMerchantsScope = <T = IMerchantsScope>(
  isTree?: boolean,
  enabled = true,
): UseQueryResult<T> => {
  const getScope = React.useCallback(
    () => api.merchants.getMerchantsScope<T>(isTree),
    [isTree],
  );

  return useQuery({
    enabled,
    queryFn: getScope,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANTS_SCOPE, isTree]),
  });
};

export const useGetMerchantRoutingSchemas = (
  id?: INullable<number>,
): UseQueryResult<IMerchantRoutingSchemas[]> => {
  const getRulesets = React.useCallback(
    () => (id ? api.merchants.getMerchantRoutingSchemas(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getRulesets,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_ROUTING_RULESETS, id]),
  });
};

export const useGetMerchantCountryGroups = (
  filters?: IAllPageFilters,
  enabled?: boolean,
): UseQueryResult<ISelectOption[]> => {
  const getCountryGroups = () => api.merchants.getMerchantCountryGroups(filters);

  return useQuery({
    enabled,
    queryFn: getCountryGroups,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_COUNTRY_GROUPS, filters]),
  });
};

export const useGetMerchantTerminalCountryGroups = (
  id?: INullable<number>,
  enabled?: boolean,
): UseQueryResult<ISelectOption[]> => {
  const getCountryGroups = React.useCallback(
    () => (id ? api.merchants.getMerchantTerminalCountryGroups(id) : null),
    [id],
  );

  return useQuery({
    enabled: !!enabled,
    queryFn: getCountryGroups,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_TERMINAL_COUNTRY_GROUPS, id]),
    throwOnError: false,
  });
};

export const useGetMerchantBinGroups = (
  filters?: IAllPageFilters,
  enabled?: boolean,
): UseQueryResult<ISelectOption[]> => {
  const getBinGroups = () => api.merchants.getMerchantBinGroups(filters);

  return useQuery({
    enabled,
    queryFn: getBinGroups,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_BIN_GROUPS, filters]),
  });
};

export const useGetMerchantTerminalBinGroups = (
  id?: INullable<number>,
  enabled?: boolean,
): UseQueryResult<ISelectOption[]> => {
  const getBinGroups = React.useCallback(
    () => (id ? api.merchants.getMerchantTerminalBinGroups(id) : null),
    [id],
  );

  return useQuery({
    enabled: !!enabled,
    queryFn: getBinGroups,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_TERMINAL_BIN_GROUPS, id]),
    throwOnError: false,
  });
};

export const useGetMerchantGroups = (
  id?: INullable<number>,
): UseQueryResult<ISelectOption[]> => {
  const getGroups = React.useCallback(
    () => (id ? api.merchants.getMerchantGroups(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getGroups,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.CLIENT_COUNTRY_GROUPS, id]),
  });
};

export const useUpdateMerchant = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IEditMerchantPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.updateMerchant,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.form.notifications.edit.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t('merchant.form.notifications.edit.loading'),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_response, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ALL]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT, id]),
      });

      notificationService.show({
        message: t('merchant.form.notifications.edit.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useCreateMerchant = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IMerchantForm,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.createMerchant,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.form.notifications.create.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t('merchant.form.notifications.create.loading'),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_response, _data, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ALL]),
      });

      notificationService.show({
        message: t('merchant.form.notifications.create.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useCopyMerchant = (): UseMutationResult<
  IBaseGist,
  QueryError,
  ICopyMerchantForm
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toastId = 'copyMerchant';

  return useMutation({
    mutationFn: api.merchants.copyMerchant,
    onError: (error: QueryError) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.form.notifications.create.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: toastId,
        });
      }
    },
    onMutate: () => {
      notificationService.show({
        message: t('merchant.form.notifications.create.loading'),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ALL]),
      });

      notificationService.show({
        message: t('merchant.form.notifications.create.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useGetMerchantCascadingSchema = (
  id?: INullable<number>,
): UseQueryResult<ICascadingSchemaDetails> => {
  const getMerchantCascadingSchema = React.useCallback(
    () => (id ? api.merchants.getMerchantCascadingSchema(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getMerchantCascadingSchema,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMA, id]),
  });
};

export const useCreateMerchantCascadingSchema = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  ICascadingSchemaPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.createMerchantCascadingSchema,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.cascadingSchema.notifications.create.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onSuccess: (_response, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMAS]),
      });
      notificationService.show({
        message: t('merchant.cascadingSchema.notifications.create.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useUpdateMerchantCascadingSchema = (
  merchantId?: string,
): UseMutationResult<
  AxiosResponse,
  QueryError,
  ICascadingSchemaPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.updateMerchantCascadingSchema,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.cascadingSchema.notifications.update.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onSuccess: (_, { id }, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMA, id]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMAS, merchantId]),
      });

      notificationService.show({
        message: t('merchant.cascadingSchema.notifications.update.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useActivateCascadingSchema = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IActivateCascadingSchemaPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.activateCascadingSchema,
    onError: (error: QueryError) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;

      notificationService.show({
        message:
          error?.response?.data?.message ||
          (isSavingError
            ? t('notifications.invalidData')
            : t('merchant.cascadingSchema.notifications.update.error')),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMAS]),
      });

      notificationService.show({
        message: t('merchant.cascadingSchema.notifications.update.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
      });
    },
  });
};

export const useCreateMerchantRoutingSchema = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IRoutingSchemaPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.createMerchantRoutingSchema,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.routingCascading.routingSchema.notifications.create.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      }
    },
    onSuccess: (_response, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ROUTING_SCHEMAS]),
      });

      notificationService.show({
        message: t(
          'merchant.routingCascading.routingSchema.notifications.create.success',
        ),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useUpdateMerchantRoutingSchema = (
  id?: INullable<number | string>,
): UseMutationResult<
  AxiosResponse,
  QueryError,
  IRoutingSchemaPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.updateMerchantRoutingSchema,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.routingCascading.routingSchema.notifications.update.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      }
    },
    onSuccess: (_response, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ROUTING_SCHEMAS, id]),
      });

      notificationService.show({
        message: t(
          'merchant.routingCascading.routingSchema.notifications.update.success',
        ),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useActivateRoutingSchema = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IActivateRoutingSchemaPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.activateRoutingSchema,
    onError: (error: QueryError) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;

      notificationService.show({
        message:
          error?.response?.data?.message ||
          (isSavingError
            ? t('notifications.invalidData')
            : t('merchant.routingCascading.routingSchema.notifications.activate.error')),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ROUTING_SCHEMAS]),
      });

      notificationService.show({
        message: t(
          'merchant.routingCascading.routingSchema.notifications.activate.success',
        ),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
      });
    },
  });
};

export const useGetApiUsers = (id?: string): UseQueryResult<IApiUser[]> => {
  const getApiUsers = React.useCallback(
    () => (id ? api.merchants.getApiUsers(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getApiUsers,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.API_USERS, id]),
  });
};

export const useCreateApiUser = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  ICreateApiUserPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.createApiUser,
    onError: (error: QueryError) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('merchant.terminalApiCredentials.notifications.create.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.API_USERS]),
      });
      notificationService.show({
        message: t('merchant.terminalApiCredentials.notifications.create.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
      });
    },
  });
};

export const useDeleteApiUser = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.deleteApiUser,
    onError: (error: QueryError) => {
      notificationService.show({
        message:
          error?.response?.data?.message ||
          t('merchant.terminalApiCredentials.notifications.delete.error'),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.API_USERS]),
      });
      notificationService.show({
        message: t('merchant.terminalApiCredentials.notifications.delete.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
      });
    },
  });
};

export const useDeleteMerchantTerminal = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number | string,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.deleteMerchant,
    onError: (error: QueryError, _variables, context) => {
      const isConflict = error?.response?.status === STATUSES.CONFLICT;

      if (!isConflict) {
        notificationService.show({
          message: t('merchants.delete.notifications.error'),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      }
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t('merchants.delete.notifications.loading'),
        title: t('notifications.loadingTitle'),
        toastId: 'deleteMerchant',
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_response, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ALL]),
      });
      notificationService.show({
        message: t('merchants.delete.notifications.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, 'deleteMerchant'),
  });
};

export const useFullDeleteMerchantTerminal = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number | string,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.merchants.fullDeleteMerchant,
    onError: (error: QueryError, _variables, context) => {
      const isConflict = error?.response?.status === STATUSES.CONFLICT;

      if (!isConflict) {
        notificationService.show({
          message:
            error?.response?.data?.message || t('merchants.delete.notifications.error'),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      }
    },
    onSuccess: (_response, _) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ALL]),
      });
    },
    throwOnError: false,
  });
};

type IUpdateMerchantSingleParameterResult = UseMutationResult<
  AxiosResponse,
  QueryError,
  IUpdateMerchantSingleParameterPayload
>;

export const useUpdateMerchantSingleParameter = (
  merchantId?: string,
): IUpdateMerchantSingleParameterResult => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const prefix = 'merchant.form.notifications.edit';
  const toastId = 'updateMerchantSingleParameter';

  return useMutation({
    mutationFn: api.merchants.updateMerchantSingleParameter,
    onError: (error: QueryError) => {
      notificationService.show({
        message: getInlineEditErrorMessage(error, t, t(`${prefix}.error`)),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${prefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ALL]),
      });
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT, merchantId]),
        });
      }

      notificationService.show({
        message: t(`${prefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useDeleteMerchantCascadingSchema = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IDeleteSchemaPayload
> => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const toastId = 'deleteMerchantCascadingSchema';
  const ntfPrefix = 'merchant.cascadingSchema.notifications.delete';

  return useMutation({
    mutationFn: api.merchants.deleteMerchantCascadingSchema,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.CASCADING_SCHEMAS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteMerchantRoutingSchema = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IDeleteSchemaPayload
> => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const toastId = 'deleteMerchantRoutingSchema';
  const ntfPrefix = 'merchant.routingCascading.routingSchema.notifications.delete';

  return useMutation({
    mutationFn: api.merchants.deleteMerchantRoutingSchema,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.ROUTING_SCHEMAS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useApiUserEdit = (
  merchantId: string,
): UseMutationResult<AxiosResponse, QueryError, IApiUserEdit> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const ntfPrefix = 'merchant.terminalApiCredentials.notifications.edit';

  return useMutation({
    mutationFn: api.merchants.editApiUser,
    onError: (error: QueryError) => {
      notificationService.show({
        message: getInlineEditErrorMessage(error, t, t(`${ntfPrefix}.error`)),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.API_USERS, merchantId]),
      });
    },
  });
};

export const useGetMerchantClientLimits = (
  id?: INullable<number | string>,
): UseQueryResult<IMerchantClientsLimits> => {
  const getMerchantClientsLimits = React.useCallback(
    () => (id ? api.merchants.getMerchantClientsLimits(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getMerchantClientsLimits,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_CLIENTS_LIMITS, id]),
  });
};

export const useCreateMerchantClientsLimits = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IMerchantClientsLimitsPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateMerchantClientsLimits';
  const ntfPrefix = 'merchant.general.clientsLimits.form.notifications.create';

  return useMutation({
    mutationFn: api.merchants.createMerchantClientsLimits,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_CLIENTS_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditMerchantClientsLimits = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IMerchantClientsLimitsPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditMerchantClientsLimits';
  const ntfPrefix = 'merchant.general.clientsLimits.form.notifications.update';

  return useMutation({
    mutationFn: api.merchants.editMerchantClientsLimits,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_CLIENTS_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteMerchantClientsLimits = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteMerchantClientsLimits';
  const ntfPrefix = 'merchant.general.clientsLimits.form.notifications.delete';

  return useMutation({
    mutationFn: api.merchants.deleteMerchantClientsLimits,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_CLIENTS_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useUpdateMerchantOrganization = (id?: string) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const toastId = 'editMerchantOrganization';
  const ntfPrefix = 'merchant.editOrganization.notifications.update';

  const mutationFn = (payload: IEditMerchantOrganizationData) =>
    api.merchants.editMerchantOrganization(payload, id);

  return useMutation({
    mutationFn,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT, id]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useGetMerchantRoutingRulesetsList = ({
  enabled = true,
  ...payload
}: IGetMerchantRoutingRulesetsPayload): UseQueryResult<IGetMerchantRoutingRulesetsResult> => {
  const { cashierPaymentMethod, id, routingSchemaId } = payload;

  const getRulesetsList = React.useCallback(() => {
    return enabled && id ? api.merchants.getMerchantRoutingRulesetsList(payload) : null;
  }, [enabled, id, payload]);

  return useQuery({
    enabled,
    queryFn: getRulesetsList,
    queryKey: normalizeQueryKey([
      MERCHANTS_KEYS.MERCHANT_RULESETS,
      id,
      routingSchemaId,
      cashierPaymentMethod,
    ]),
  });
};

export const useGetMerchantOutputChannelsList = (
  id?: number | string,
): UseQueryResult<IGetMerchantOutputChannelsResult> => {
  const getRulesetsList = React.useCallback(() => {
    return id ? api.merchants.getMerchantOutputChannelsList(id) : null;
  }, [id]);

  return useQuery({
    queryFn: getRulesetsList,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNELS_LIST, id]),
  });
};

export const useGetMerchantOutputChannels = ({
  additionalKeys = [],
  pageFilters,
}: IUseGetGridData): UseQueryResult<IPageType<IOutputChannel>> =>
  useGetAllEntities<IOutputChannel>({
    getAllEntitiesFunc: api.merchants.getMerchantOutputChannels,
    pageFilters,
    queryKey: [MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNELS, ...additionalKeys],
  });

export const useGetMerchantOutputChannel = (
  id?: INullable<number>,
): UseQueryResult<IOutputChannel> => {
  const getOutputChannelDetails = React.useCallback(
    () => (id ? api.merchants.getMerchantOutputChannel(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getOutputChannelDetails,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNEL, id]),
  });
};

export const useCreateMerchantOutputChannel = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IOutputChannelPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateMerchantOutputChannel';
  const ntfPrefix = 'merchant.terminalOutputChannels.notifications.create';

  return useMutation({
    mutationFn: api.merchants.createMerchantOutputChannel,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (_, { payload }) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([
          MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNELS,
          payload.merchantTerminalId,
        ]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditMerchantOutputChannel = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IOutputChannelPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditMerchantOutputChannel';
  const ntfPrefix = 'merchant.terminalOutputChannels.notifications.edit';

  return useMutation({
    mutationFn: api.merchants.editMerchantOutputChannel,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (_, { id, payload }) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([
          MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNELS,
          payload.merchantTerminalId,
        ]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNEL, id]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useVerifyMerchantOutputChannel = (
  additionalKeys?: string[],
): UseMutationResult<IBaseGist, QueryError, IVerifyMerchantOutputChannelPayload> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'VerifyMerchantOutputChannel';
  const ntfPrefix = 'merchant.terminalOutputChannels.notifications.verify';

  return useMutation({
    mutationFn: api.merchants.verifyOutputChannel,
    onError: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      if (additionalKeys?.length) {
        queryClient.invalidateQueries({ queryKey: normalizeQueryKey(additionalKeys) });
      }
      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: false,
  });
};

export const useResendMerchantOtp = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number | string
> => {
  const { t } = useTranslation();

  const toastId = 'resendMerchantOtp';
  const ntfPrefix = 'merchant.terminalOutputChannels.notifications.resend';

  return useMutation({
    mutationFn: api.merchants.resendOtp,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useDeleteMerchantOutputChannel = (
  merchantId?: string,
): UseMutationResult<AxiosResponse, QueryError, number | string> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteMerchantOutputChannel';
  const ntfPrefix = 'merchant.terminalOutputChannels.notifications.delete';

  return useMutation({
    mutationFn: api.merchants.deleteMerchantOutputChannel,
    onError: (error: QueryError) => {
      showMutationErrorNotification({
        error,
        fallbackMessage: t(`${ntfPrefix}.error`),
        getTranslation: t,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: normalizeQueryKey([
            MERCHANTS_KEYS.MERCHANT_OUTPUT_CHANNELS,
            merchantId,
          ]),
        });
      }

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useMerchantsGroupEdit = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IGroupEditPayload
> => {
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'groupEditMerchants';
  const ntfPrefix = 'merchant.form.notifications.groupEdit';

  return useMutation({
    mutationFn: api.merchants.groupEditMerchants,
    onError: (error: QueryError) => {
      notificationService.show({
        message:
          translateServerMessage({
            getTranslation: t,
            i18n,
            message: error?.response?.data?.message,
          }) || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            query.queryKey.includes(MERCHANTS_KEYS.ALL) ||
            query.queryKey.includes(MERCHANTS_KEYS.MERCHANT)
          );
        },
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useGetMerchantCustomActions = ({
  additionalKeys = [],
  pageFilters,
}: IUseGetGridData): UseQueryResult<IPageType<ICustomAction>> =>
  useGetAllEntities<ICustomAction>({
    getAllEntitiesFunc: api.merchants.getMerchantCustomActions,
    pageFilters,
    queryKey: [MERCHANTS_KEYS.MERCHANT_CUSTOM_ACTIONS, ...additionalKeys],
  });

export const useGetMerchantCustomAction = (
  id?: INullable<number>,
): UseQueryResult<ICustomAction> => {
  const getCustomActionDetails = React.useCallback(
    () => (id ? api.merchants.getMerchantCustomAction(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getCustomActionDetails,
    queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_CUSTOM_ACTION, id]),
  });
};

export const useCreateMerchantCustomAction = (): UseMutationResult<
  IBaseGist,
  QueryError,
  ICustomActionPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateMerchantCustomAction';
  const ntfPrefix = 'merchant.terminalCustomActions.notifications.create';

  return useMutation({
    mutationFn: api.merchants.createMerchantCustomAction,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (_, { payload }) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([
          MERCHANTS_KEYS.MERCHANT_CUSTOM_ACTIONS,
          payload.merchantTerminalId,
        ]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditMerchantCustomAction = (): UseMutationResult<
  IBaseGist,
  QueryError,
  ICustomActionPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditMerchantCustomAction';
  const ntfPrefix = 'merchant.terminalCustomActions.notifications.edit';

  return useMutation({
    mutationFn: api.merchants.editMerchantCustomAction,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (_, { id, payload }) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([
          MERCHANTS_KEYS.MERCHANT_CUSTOM_ACTIONS,
          payload.merchantTerminalId,
        ]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([MERCHANTS_KEYS.MERCHANT_CUSTOM_ACTION, id]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteMerchantCustomAction = (
  merchantId?: string,
): UseMutationResult<AxiosResponse, QueryError, INullable<number>> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteMerchantCustomAction';
  const ntfPrefix = 'merchant.terminalCustomActions.notifications.delete';

  return useMutation({
    mutationFn: api.merchants.deleteMerchantCustomAction,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      if (merchantId) {
        queryClient.invalidateQueries({
          queryKey: normalizeQueryKey([
            MERCHANTS_KEYS.MERCHANT_CUSTOM_ACTIONS,
            merchantId,
          ]),
        });
      }

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};
